import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import type { TemplateConfig, TemplateLayer, LayerPosition } from '../types';

// ─── Special IDs for headline / subheadline ───────────────────
// These are NOT in editableConfig.layers — they're top-level config fields.
// We use synthetic IDs so the same selection mechanism covers both.

export const TEXT_ELEMENT_IDS = {
  HEADLINE: '__headline',
  SUBHEADLINE: '__subheadline',
} as const;

export type TextElementId = typeof TEXT_ELEMENT_IDS[keyof typeof TEXT_ELEMENT_IDS];

export function isTextElementId(id: string | null): id is TextElementId {
  return id === TEXT_ELEMENT_IDS.HEADLINE || id === TEXT_ELEMENT_IDS.SUBHEADLINE;
}

// ─── State ───────────────────────────────────────────────────

export type EditorMode = 'edit' | 'preview';

interface EditorState {
  editableConfig: TemplateConfig | null;
  /** Real layer ID, '__headline', '__subheadline', or null */
  selectedLayerId: string | null;
  mode: EditorMode;
  zoom: number;
  snapGrid: boolean;
  history: TemplateConfig[];
  historyIndex: number;
}

const initialState: EditorState = {
  editableConfig: null,
  selectedLayerId: null,
  mode: 'edit',
  zoom: 1,
  snapGrid: false,
  history: [],
  historyIndex: -1,
};

// ─── Actions ─────────────────────────────────────────────────

type EditorAction =
  | { type: 'LOAD_TEMPLATE'; config: TemplateConfig }
  | { type: 'SELECT_LAYER'; id: string | null }
  | { type: 'UPDATE_LAYER_POSITION'; id: string; position: Partial<LayerPosition> }
  | { type: 'UPDATE_LAYER'; id: string; patch: Record<string, unknown> }
  | { type: 'TOGGLE_LAYER_VISIBILITY'; id: string }
  | { type: 'TOGGLE_LAYER_LOCK'; id: string }
  | { type: 'REORDER_LAYERS'; layers: TemplateLayer[] }
  | { type: 'ADD_LAYER'; layer: TemplateLayer }
  | { type: 'REMOVE_LAYER'; id: string }
  | { type: 'UPDATE_TEXT_ELEMENT'; id: TextElementId; patch: Record<string, unknown> }
  | { type: 'TOGGLE_TEXT_ELEMENT_VISIBILITY'; id: TextElementId }
  | { type: 'UPDATE_DURATION'; seconds: number }
  | { type: 'SET_MODE'; mode: EditorMode }
  | { type: 'SET_ZOOM'; zoom: number }
  | { type: 'SET_SNAP_GRID'; enabled: boolean }
  | { type: 'UNDO' }
  | { type: 'REDO' };

// ─── Pure helpers ─────────────────────────────────────────────

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

function patchLayer(
  layers: TemplateLayer[],
  id: string,
  patch: (layer: TemplateLayer) => TemplateLayer
): TemplateLayer[] {
  return layers.map((l) => (l.id === id ? patch(l) : l));
}

function pushHistory(state: EditorState, config: TemplateConfig): EditorState {
  const trimmed = state.history.slice(0, state.historyIndex + 1);
  const history = [...trimmed, config].slice(-50);
  return { ...state, history, historyIndex: history.length - 1 };
}

// ─── Reducer ─────────────────────────────────────────────────

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'LOAD_TEMPLATE': {
      const config = deepClone(action.config);
      return {
        ...initialState,
        editableConfig: config,
        history: [config],
        historyIndex: 0,
      };
    }

    case 'SELECT_LAYER':
      return { ...state, selectedLayerId: action.id };

    case 'UPDATE_LAYER_POSITION': {
      if (!state.editableConfig) return state;

      // Handle text elements (headline / subheadline)
      if (isTextElementId(action.id)) {
        const key = action.id === TEXT_ELEMENT_IDS.HEADLINE ? 'headline' : 'subheadline';
        const updated: TemplateConfig = {
          ...state.editableConfig,
          [key]: {
            ...state.editableConfig[key],
            position: { ...state.editableConfig[key].position, ...action.position },
          },
        };
        return pushHistory({ ...state, editableConfig: updated }, updated);
      }

      // Regular layer
      const updated: TemplateConfig = {
        ...state.editableConfig,
        layers: patchLayer(state.editableConfig.layers, action.id, (l) => ({
          ...l,
          position: { ...l.position, ...action.position },
        })),
      };
      return pushHistory({ ...state, editableConfig: updated }, updated);
    }

    case 'UPDATE_LAYER': {
      if (!state.editableConfig) return state;
      const updated: TemplateConfig = {
        ...state.editableConfig,
        layers: patchLayer(state.editableConfig.layers, action.id, (l) => ({
          ...l,
          ...action.patch,
        })),
      };
      return pushHistory({ ...state, editableConfig: updated }, updated);
    }

    case 'UPDATE_TEXT_ELEMENT': {
      if (!state.editableConfig) return state;
      const key = action.id === TEXT_ELEMENT_IDS.HEADLINE ? 'headline' : 'subheadline';
      const updated: TemplateConfig = {
        ...state.editableConfig,
        [key]: { ...state.editableConfig[key], ...action.patch },
      };
      return pushHistory({ ...state, editableConfig: updated }, updated);
    }

    case 'TOGGLE_TEXT_ELEMENT_VISIBILITY': {
      if (!state.editableConfig) return state;
      const key = action.id === TEXT_ELEMENT_IDS.HEADLINE ? 'headline' : 'subheadline';
      const updated: TemplateConfig = {
        ...state.editableConfig,
        [key]: {
          ...state.editableConfig[key],
          visible: !(state.editableConfig[key].visible ?? true),
        },
      };
      return { ...state, editableConfig: updated };
    }

    case 'UPDATE_DURATION': {
      if (!state.editableConfig) return state;
      const seconds = Math.max(1, Math.round(action.seconds));
      const updated: TemplateConfig = {
        ...state.editableConfig,
        metadata: { ...state.editableConfig.metadata, duration: seconds },
      };
      return pushHistory({ ...state, editableConfig: updated }, updated);
    }

    case 'TOGGLE_LAYER_VISIBILITY': {
      if (!state.editableConfig) return state;
      const updated: TemplateConfig = {
        ...state.editableConfig,
        layers: patchLayer(state.editableConfig.layers, action.id, (l) => ({
          ...l,
          visible: !l.visible,
        })),
      };
      return { ...state, editableConfig: updated };
    }

    case 'TOGGLE_LAYER_LOCK': {
      if (!state.editableConfig) return state;
      const updated: TemplateConfig = {
        ...state.editableConfig,
        layers: patchLayer(state.editableConfig.layers, action.id, (l) => ({
          ...l,
          locked: !l.locked,
        })),
      };
      return { ...state, editableConfig: updated };
    }

    case 'REORDER_LAYERS': {
      if (!state.editableConfig) return state;
      const updated: TemplateConfig = {
        ...state.editableConfig,
        layers: action.layers,
      };
      return pushHistory({ ...state, editableConfig: updated }, updated);
    }

    case 'ADD_LAYER': {
      if (!state.editableConfig) return state;
      const updated: TemplateConfig = {
        ...state.editableConfig,
        layers: [...state.editableConfig.layers, action.layer],
      };
      return pushHistory({ ...state, editableConfig: updated, selectedLayerId: action.layer.id }, updated);
    }

    case 'REMOVE_LAYER': {
      if (!state.editableConfig) return state;
      const updated: TemplateConfig = {
        ...state.editableConfig,
        layers: state.editableConfig.layers.filter((l) => l.id !== action.id),
      };
      return pushHistory(
        { ...state, editableConfig: updated, selectedLayerId: state.selectedLayerId === action.id ? null : state.selectedLayerId },
        updated
      );
    }

    case 'SET_MODE':
      return { ...state, mode: action.mode, selectedLayerId: null };

    case 'SET_ZOOM':
      return { ...state, zoom: Math.min(2, Math.max(0.1, action.zoom)) };

    case 'SET_SNAP_GRID':
      return { ...state, snapGrid: action.enabled };

    case 'UNDO': {
      const idx = Math.max(0, state.historyIndex - 1);
      return {
        ...state,
        editableConfig: deepClone(state.history[idx]),
        historyIndex: idx,
        selectedLayerId: null,
      };
    }

    case 'REDO': {
      const idx = Math.min(state.history.length - 1, state.historyIndex + 1);
      return {
        ...state,
        editableConfig: deepClone(state.history[idx]),
        historyIndex: idx,
        selectedLayerId: null,
      };
    }

    default:
      return state;
  }
}

// ─── Selected text element helper ────────────────────────────

export interface SelectedTextElement {
  id: TextElementId;
  label: string;
  config: TemplateConfig['headline']; // headline & subheadline share the same shape
}

// ─── Context ─────────────────────────────────────────────────

interface EditorContextValue {
  state: EditorState;
  loadTemplate: (config: TemplateConfig) => void;
  selectLayer: (id: string | null) => void;
  updateLayerPosition: (id: string, position: Partial<LayerPosition>) => void;
  updateLayer: (id: string, patch: Record<string, unknown>) => void;
  updateTextElement: (id: TextElementId, patch: Record<string, unknown>) => void;
  toggleTextElementVisibility: (id: TextElementId) => void;
  updateDuration: (seconds: number) => void;
  toggleLayerVisibility: (id: string) => void;
  toggleLayerLock: (id: string) => void;
  reorderLayers: (layers: TemplateLayer[]) => void;
  addLayer: (layer: TemplateLayer) => void;
  removeLayer: (id: string) => void;
  setMode: (mode: EditorMode) => void;
  setZoom: (zoom: number) => void;
  setSnapGrid: (enabled: boolean) => void;
  undo: () => void;
  redo: () => void;
  /** Non-null when a real layer (not a text element) is selected */
  selectedLayer: TemplateLayer | null;
  /** Non-null when headline or subheadline is selected */
  selectedTextElement: SelectedTextElement | null;
  canUndo: boolean;
  canRedo: boolean;
}

const EditorContext = createContext<EditorContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────

export function EditorProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(editorReducer, initialState);

  const loadTemplate = useCallback(
    (config: TemplateConfig) => dispatch({ type: 'LOAD_TEMPLATE', config }), []
  );
  const selectLayer = useCallback(
    (id: string | null) => dispatch({ type: 'SELECT_LAYER', id }), []
  );
  const updateLayerPosition = useCallback(
    (id: string, position: Partial<LayerPosition>) =>
      dispatch({ type: 'UPDATE_LAYER_POSITION', id, position }), []
  );
  const updateLayer = useCallback(
    (id: string, patch: Record<string, unknown>) =>
      dispatch({ type: 'UPDATE_LAYER', id, patch }), []
  );
  const updateTextElement = useCallback(
    (id: TextElementId, patch: Record<string, unknown>) =>
      dispatch({ type: 'UPDATE_TEXT_ELEMENT', id, patch }), []
  );
  const toggleTextElementVisibility = useCallback(
    (id: TextElementId) => dispatch({ type: 'TOGGLE_TEXT_ELEMENT_VISIBILITY', id }), []
  );
  const updateDuration = useCallback(
    (seconds: number) => dispatch({ type: 'UPDATE_DURATION', seconds }), []
  );
  const toggleLayerVisibility = useCallback(
    (id: string) => dispatch({ type: 'TOGGLE_LAYER_VISIBILITY', id }), []
  );
  const toggleLayerLock = useCallback(
    (id: string) => dispatch({ type: 'TOGGLE_LAYER_LOCK', id }), []
  );
  const reorderLayers = useCallback(
    (layers: TemplateLayer[]) => dispatch({ type: 'REORDER_LAYERS', layers }), []
  );
  const addLayer = useCallback(
    (layer: TemplateLayer) => dispatch({ type: 'ADD_LAYER', layer }), []
  );
  const removeLayer = useCallback(
    (id: string) => dispatch({ type: 'REMOVE_LAYER', id }), []
  );
  const setMode = useCallback(
    (mode: EditorMode) => dispatch({ type: 'SET_MODE', mode }), []
  );
  const setZoom = useCallback(
    (zoom: number) => dispatch({ type: 'SET_ZOOM', zoom }), []
  );
  const setSnapGrid = useCallback(
    (enabled: boolean) => dispatch({ type: 'SET_SNAP_GRID', enabled }), []
  );
  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);

  const selectedLayer = useMemo(
    () =>
      state.editableConfig && !isTextElementId(state.selectedLayerId)
        ? state.editableConfig.layers.find((l) => l.id === state.selectedLayerId) ?? null
        : null,
    [state.editableConfig, state.selectedLayerId]
  );

  const selectedTextElement = useMemo((): SelectedTextElement | null => {
    if (!state.editableConfig || !isTextElementId(state.selectedLayerId)) return null;
    const id = state.selectedLayerId;
    const isHeadline = id === TEXT_ELEMENT_IDS.HEADLINE;
    return {
      id,
      label: isHeadline ? 'Headline' : 'Subheadline',
      config: isHeadline
        ? state.editableConfig.headline
        : state.editableConfig.subheadline,
    };
  }, [state.editableConfig, state.selectedLayerId]);

  const canUndo = state.historyIndex > 0;
  const canRedo = state.historyIndex < state.history.length - 1;

  const value: EditorContextValue = {
    state,
    loadTemplate,
    selectLayer,
    updateLayerPosition,
    updateLayer,
    updateTextElement,
    toggleTextElementVisibility,
    updateDuration,
    toggleLayerVisibility,
    toggleLayerLock,
    reorderLayers,
    addLayer,
    removeLayer,
    setMode,
    setZoom,
    setSnapGrid,
    undo,
    redo,
    selectedLayer,
    selectedTextElement,
    canUndo,
    canRedo,
  };

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

// ─── Hook ────────────────────────────────────────────────────

export function useEditorContext(): EditorContextValue {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useEditorContext must be used inside <EditorProvider>');
  return ctx;
}
